<template>
  <div
    v-if="isLanding"
    class="relative overflow-hidden z-20"
    :class="isHome ? ' h-[calc(100vh-88px)]' : 'h-[calc(100vh-142px)]'"
  >
    <MediaImage
      v-if="image"
      v-bind="image"
      :image-style="imageStyles"
      preload
      loading="eager"
      class="relative size-full"
      :img-class="'size-full object-cover'"
    />

    <MediaRemoteVideo
      v-else-if="remoteVideo"
      :video="remoteVideo"
      :is-landing="isLanding"
      :image-style="imageStyles"
      is-hero
      muted
      loop
      autoplay
      controls
    />

    <MediaVideo
      v-else-if="video"
      v-bind="video"
      ref="mediaVideo"
      :video="video.video"
      :video-mobile="video.videoMobile"
      autoplay
      loop
      :controls="false"
      :poster="video.poster"
      muted
      :image-style="imageStyles"
      :is-landing="isLanding"
      is-hero
    />
    <div class="gradient-overlay" />
    <Container>
      <div class="grid-container absolute bottom-6 z-40 pointer-events-none">
        <h1
          v-blokkli-editable:title
          class="col-span-4 text-4xl text-white sm:col-span-6 md:col-span-6 md:text-5xl lg:col-span-8 xl:text-6xl mb-4"
          :class="isHome ? 'max-w-[468px]' : ''"
        >
          {{ title }}
        </h1>
        <div class="flex flex-row justify-between col-span-8">
          <SkipToMainContent />
        </div>
      </div>
    </Container>
  </div>
  <div v-else class="grid-container pt-6 md:pt-20">
    <div
      class="relative aspect-[358/255] md:aspect-video flex items-center justify-center h-full col-span-4 sm:col-span-6 md:col-span-8 lg:col-span-12 overflow-hidden"
    >
      <MediaImage
        v-if="image"
        v-bind="image"
        preload
        :image-style="imageStylesNormal"
        loading="eager"
        class="relative w-full"
      />

      <MediaRemoteVideo
        v-else-if="remoteVideo"
        class="flex items-center justify-center h-full hero-media"
        :video="remoteVideo"
        :is-landing="isLanding"
        :image-style="imageStylesNormal"
        is-hero
      />

      <MediaVideo
        v-else-if="video"
        v-bind="video"
        ref="mediaVideo"
        :video="video.video"
        :video-mobile="video.videoMobile"
        controls
        :image-style="imageStylesNormal"
        :is-landing="isLanding"
        is-hero
        :poster="video.poster"
        :autoplay="false"
        :muted="false"
        :loop="false"
      />
      <div
        v-if="tag"
        class="absolute left-8 top-6 bg-lightGreenBg text-darkGreen rounded-full h-14 px-7 mr-2 flex items-center justify-center transition-all duration-300 shadow-lg border-none cursor-pointer"
      >
        {{ tag }}
      </div>
    </div>
    <div
      class="pt-16 col-span-4 sm:col-span-6 md:col-span-6 md:col-start-2 lg:col-span-8 lg:col-start-3"
    >
      <h1 v-blokkli-editable:title class="pb-6 md:pb-12 text-4xl md:text-7xl">
        {{ title }}
      </h1>
      <div v-if="lead">
        <div class="pb-10">
          <div
            v-blokkli-editable:field_lead
            class="text-xl md:text-3xl leading-6 md:leading-10 md:font-bold page-landing:lg:text-base page-landing:lg:leading-6"
            v-html="lead"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import type {
  MediaImageFragment,
  NodePageFragment,
  MediaVideoFragment,
  MediaRemoteVideoFragment,
} from '#graphql-operations'

const props = defineProps<{
  title?: string
  lead?: string
  media?: NodePageFragment['hero']
  isLanding?: boolean
  isHome?: boolean
  tag?: string
}>()

// check hero type
const image = computed(() => {
  if (props.media && 'image' in props.media) {
    return props.media as MediaImageFragment
  }
  return null
})

const video = computed(() => {
  if (props.media && 'video' in props.media) {
    return props.media as MediaVideoFragment
  }
  return null
})

const remoteVideo = computed(() => {
  if (props.media && 'url' in props.media) {
    return props.media as MediaRemoteVideoFragment
  }
  return null
})

const imageStyles = defineImageStyle({
  type: 'pictures',
  pictures: {
    xs: {
      width: 768,
      aspectRatio: 1 / 2,
    },
    sm: {
      width: 1024,
      aspectRatio: 1 / 2,
    },
    md: {
      width: 1280,
      aspectRatio: 1,
    },
    lg: {
      width: 1380,
      aspectRatio: 16 / 9,
    },
    xl: {
      width: 2561,
      aspectRatio: 16 / 9,
    },
  },
})

const imageStylesNormal = defineImageStyle({
  type: 'pictures',
  pictures: {
    xs: {
      width: 768,
      aspectRatio: 358 / 255,
    },
    sm: {
      width: 1024,
      aspectRatio: 358 / 255,
    },
    md: {
      width: 1280,
      aspectRatio: 1380 / 794,
    },
    lg: {
      width: 1380,
      aspectRatio: 1380 / 794,
    },
    xl: {
      width: 2561,
      aspectRatio: 1380 / 794,
    },
  },
})
</script>

<style>
@reference "~/assets/css/tailwind.css";

.hero-media {
  @apply mx-auto;

  @supports (height: 100svh) {
    /* Use svh unit. 100svh is the smallest possible viewport height. Basically */
    /* the height of the viewport minus any elements that may appear or disappear */
    /* during scrolling (address bar, tab/nav bars, etc.) */
    --small-viewport-height: 100svh;
    /* Since we can use the svh unit, we don't need the fixed browser navbar size anymore. */
    --hero-height-browser-navbar: 0px;
  }

  /* Assume that browsers from this viewport on that don't support svh anyway */
  /* don't display any navbars that overlap 100vh. */
  @media (width >= theme(--breakpoint-md)) {
    --hero-height-browser-navbar: 0px;
  }

  /* The ideal height for the hero so that: */
  /* - it fills the screen top to bottom of the viewport */
  /* - makes sure the anchor nav is always visible */
  max-height: 177vw;
  min-height: 500px;
  @media (width >= theme(--breakpoint-xs)) {
    max-height: 80vw;
  }
  video {
    width: 100%;
    max-width: none;
  }

  picture {
    @apply absolute top-0 left-0 size-full;

    img {
      @apply relative size-full object-cover;
    }
  }

  /* hide all clutter */
  .vjs-controls-disabled .vjs-control-bar,
  .vjs-loading-spinner,
  .vjs-hidden {
    display: none !important;
  }

  .video-js.vjs-fluid {
    height: 0;
    width: 100%;
  }
  .video-js .vjs-tech {
    left: 0;
    position: absolute;
    top: 0;
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
}
</style>
